import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '#pplx/frontend/aether/components/Button/index.js';
import { createHomeComposerFocusHandle } from '#pplx/frontend/apps/ask/ask-composer/createHomeComposerFocusHandle.js';
import { useCallbackRef } from '#pplx/frontend/common/hooks/useCallbackRef.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import classNames from 'classnames';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useIntl } from 'react-intl';
import { RemoveScroll } from 'react-remove-scroll';
function openHomeMobileOverlay({ onOpen, refocusInput, }) {
    if (document.startViewTransition) {
        try {
            const transition = document.startViewTransition({
                update: () => flushSync(onOpen),
                types: ['mweb-home-overlay'],
            });
            transition.finished.then(refocusInput).catch(() => { });
        }
        catch {
            // Older transition APIs would fade the whole page, so open without animating.
            onOpen();
        }
    }
    else {
        onOpen();
    }
}
export function HomeMobileOverlayShell({ children, isActive, recessedFrame, onBack, overlayHandlers, }) {
    return (_jsx(RemoveScroll, { removeScrollBar: false, enabled: isActive, allowPinchZoom: true, children: _jsxs("div", { className: classNames(isActive && ['fixed inset-0 z-50', 'bg-base', 'px-2 pb-2']), ...overlayHandlers, children: [isActive && (_jsx("div", { className: "h-headerHeight flex items-center gap-1", children: _jsx(OverlayBackButton, { onBack: onBack }) })), _jsx("div", { className: classNames({
                        '-mt-[5px]': recessedFrame,
                        'absolute inset-x-0 bottom-2 -mx-2': !isActive,
                        // Keep the same side gutter while the overlay opens and closes.
                        'px-1.5': recessedFrame,
                    }), style: { viewTransitionName: 'mweb-home-ask-input' }, children: children })] }) }));
}
function OverlayBackButton({ onBack }) {
    const { $t } = useIntl();
    return (_jsx("div", { className: "-ml-1 animate-in fade-in duration-quick ease-out", children: _jsx(Button, { icon: $icon('arrow-left'), variant: "text", size: "default", rounded: true, "aria-label": $t({
                defaultMessage: 'Close',
                id: 'a3cee46161',
                description: 'Aria label for the back button to dismiss the mobile search overlay',
            }), onClick: onBack }) }));
}
// eslint-disable-next-line react/only-export-components -- Keep the mobile overlay lifecycle beside its shared shell.
export function useHomeComposerMobileOverlay({ isMobileWeb, onActivateBlankSuggestions, resetAutosuggestions, onClear, onCommandMenuOpenChange, }) {
    const composerRef = useRef(null);
    const inputFocusRef = useMemo(() => ({ current: createHomeComposerFocusHandle(composerRef) }), [composerRef]);
    const { isOverlayActive: isActive, closeOverlay, overlayHandlers, } = useMobileSearchOverlay({
        onTouchFocus: () => inputFocusRef.current?.focus(),
        onRefocus: () => inputFocusRef.current?.focus(),
        getIsEditableFocused: () => inputFocusRef.current?.getFocusState()?.isFocused ?? false,
    });
    // Handle touch on the editor so model and upload controls cannot open the overlay.
    // Keep pointer and focus on the shell for mouse clicks.
    const { onTouchStart, onPointerDown, onFocus } = overlayHandlers;
    const wasActive = useRef(isActive);
    useLayoutEffect(() => {
        if (wasActive.current === true && isActive === false) {
            // Cancel suggestions and close menus when Back dismisses the overlay.
            resetAutosuggestions();
            onCommandMenuOpenChange?.(false);
        }
        wasActive.current = isActive;
    }, [isActive, onCommandMenuOpenChange, resetAutosuggestions]);
    const onEditableTouchStart = useCallback((event) => {
        // Touch prevents the click that normally loads blank suggestions.
        if (onTouchStart(event))
            onActivateBlankSuggestions();
    }, [onActivateBlankSuggestions, onTouchStart]);
    const onBack = useCallback(() => {
        onClear();
        closeOverlay();
    }, [closeOverlay, onClear]);
    return useMemo(() => ({
        composerRef,
        composerProps: { onEditableTouchStart: isMobileWeb ? onEditableTouchStart : undefined },
        shellProps: isMobileWeb
            ? { isActive, onBack, overlayHandlers: { onPointerDown, onFocus } }
            : null,
    }), [composerRef, isActive, isMobileWeb, onBack, onEditableTouchStart, onFocus, onPointerDown]);
}
// eslint-disable-next-line react/only-export-components -- Both input adapters share the overlay lifecycle and shell.
export function useMobileSearchOverlay({ onTouchFocus, onRefocus, getIsEditableTarget, getIsEditableFocused, }) {
    const [isOverlayActive, setIsOverlayActive] = useState(false);
    const tappedTextInput = useRef(false);
    const focusTouch = useCallbackRef(onTouchFocus);
    const refocusInput = useCallbackRef(onRefocus);
    const isEditableTarget = useCallbackRef((target) => getIsEditableTarget?.(target) ?? true);
    const isEditableFocused = useCallbackRef(() => getIsEditableFocused?.() ?? true);
    const closeOverlay = useCallback(() => {
        setIsOverlayActive(false);
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
    }, []);
    const openOverlay = useCallback(() => {
        openHomeMobileOverlay({
            onOpen: () => setIsOverlayActive(true),
            refocusInput,
        });
    }, [refocusInput]);
    const handleTouchStart = useCallback((e) => {
        if (isOverlayActive)
            return false;
        // Ignore touches on controls outside the editor.
        if (!isEditableTarget(e.target))
            return false;
        // Stop the browser from scrolling the input before the overlay moves it.
        e.preventDefault();
        tappedTextInput.current = false;
        // Focus during the touch event so Safari opens the keyboard.
        focusTouch();
        openOverlay();
        return true;
    }, [isOverlayActive, isEditableTarget, focusTouch, openOverlay]);
    const handlePointerDown = useCallback((e) => {
        tappedTextInput.current = isEditableTarget(e.target);
    }, [isEditableTarget]);
    // Mouse clicks open the overlay on focus. Touch opens it earlier and clears
    // this flag to avoid opening twice.
    const handleFocus = useCallback(() => {
        if (!tappedTextInput.current)
            return;
        tappedTextInput.current = false;
        if (!isEditableFocused() || isOverlayActive)
            return;
        setIsOverlayActive(true);
    }, [isEditableFocused, isOverlayActive]);
    return {
        isOverlayActive,
        closeOverlay,
        overlayHandlers: {
            onTouchStart: handleTouchStart,
            onPointerDown: handlePointerDown,
            onFocus: handleFocus,
        },
    };
}
//# sourceMappingURL=homeMobileOverlay.js.map